// @flow

import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';
import {
  Divider,
  Grid,
  Hidden,
  IconButton,
  Typography,
  makeStyles,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';

import VariantOptionValueItem from '../VariantOptionValueItem/VariantOptionValueItem';
import {Edit, Delete, HelpOutline} from '@material-ui/icons';
import VariantItemPreviewModal from './VariantItemPreviewModal';

import type {ProductVariantOption} from '../../../../entities';
import {deleteProductVariantOptionService} from '../../services';
import {useActions} from '../../../../utils/globalStore';

import {actions} from '../../../products/duck';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';

import {useToggle} from '../../../../utils/react';
import {useListProducts} from '../../../products/utils/hooks';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';
import {getImageUrl} from '../../../../utils/image_provider';
import {useToastActions} from '../../../toast-alert/utils';

type Props = {
  variantOption: ProductVariantOption,
};

const useStyles = makeStyles(theme => ({
  root: {
    border: `1px solid ${colors.grey300}`,
    borderRadius: 12,
    padding: theme.spacing(1),
    position: 'relative',
  },
  content: {
    width: '100%',
  },
  mobileHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  name: {
    color: colors.grey900,
    fontSize: 16,
  },
  options: {
    fontSize: 14,
    color: colors.grey700,
  },
  divider: {
    backgroundColor: colors.grey300,
    margin: theme.spacing(1, 0),
  },
  list: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
  },
  btn: {
    margin: theme.spacing(1, 0),
    color: colors.grey800,
    borderRadius: 0,
    background: colors.grey200,
  },
  editBtn: {},
  deleteBtn: {
    color: colors.danger,
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  modalList: {
    maxHeight: 300,
    overflow: 'auto',
  },
}));

export const MAX_OPTION_VALUES_TO_SHOW = 20;

const VariantItem = ({variantOption}: Props) => {
  const history = useHistory();
  const classes = useStyles();

  const [loading, setLoading] = useState(false);

  const {showToast} = useToastActions();

  const {fetchProducts, loading: listingProducts, products} = useListProducts();
  const [confirmModalOpen, toggleConfirmModalOpen] = useToggle();

  // const isFromExternalResource = !!variantOption?.metadataSetting?.source;
  const isFromExternalResource = false;

  const values = variantOption.values;

  const fetchProductVariantOptions = useActions(
    actions.fetchProductVariantOptions
  );

  const namingDetails = (
    <div>
      <Typography className={classes.name}>{variantOption.label}</Typography>
      <Typography className={classes.options}>
        Opciones: {variantOption.values.length}
      </Typography>
    </div>
  );

  const variantModalTrigger = (
    <VariantItemPreviewModal variantOption={variantOption} />
  );

  const handleDeleteProductVariantOption = () => {
    fetchProducts({
      variant_options: [variantOption.id],
    }).then(() => {
      toggleConfirmModalOpen();
    });
  };

  const valuesList = (
    <div className={classes.list}>
      {values.slice(0, MAX_OPTION_VALUES_TO_SHOW).map(v => (
        <VariantOptionValueItem
          key={v.id}
          imageShape={variantOption.imageShape}
          displayType={variantOption.displayType}
          value={v}
        />
      ))}
      <Hidden smDown>{variantModalTrigger}</Hidden>
    </div>
  );

  const variantActions = (
    <div>
      <IconButton
        className={classes.editBtn}
        onClick={() => history.push(`/products-variants/${variantOption.id}`)}
      >
        <Edit />
      </IconButton>
      <IconButton
        className={classes.deleteBtn}
        onClick={handleDeleteProductVariantOption}
      >
        <Delete />
      </IconButton>
    </div>
  );

  return (
    <div className={classes.root}>
      {(loading || listingProducts) && (
        <div className={classes.loader}>
          <InlineLoader size={40} loading />
        </div>
      )}
      <div className={classes.content}>
        {/* Desktop */}
        <Hidden smDown>
          <Grid container spacing={1}>
            <Grid item xs={3}>
              {namingDetails}
            </Grid>
            <div style={{flex: 1}}>{valuesList}</div>
            {!isFromExternalResource && <Grid>{variantActions}</Grid>}
          </Grid>
        </Hidden>
        {/* Mobile */}
        <Hidden mdUp>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <div className={classes.mobileHeader}>
                {namingDetails}
                {variantActions}
              </div>

              <Divider className={classes.divider} />

              <Grid>{valuesList}</Grid>

              <Grid>
                <div style={{margin: '8px 0'}}>{variantModalTrigger}</div>
              </Grid>
            </Grid>
          </Grid>
        </Hidden>
      </div>
      {confirmModalOpen && (
        <ConfirmModal
          open={confirmModalOpen}
          title="Esta acción no se puede deshacer"
          message={
            <div style={{marginTop: '16px'}}>
              {products.length === 0 ? (
                <>
                  <Typography
                    variant="body2"
                    align="center"
                    style={{fontWeight: 'bold'}}
                  >
                    ¿Estás seguro de eliminar esta opción?
                  </Typography>

                  <Typography variant="body2" style={{marginTop: '16px'}}>
                    Esta opción no está asociada a ningún producto
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2">
                    Al eliminar esta opción, se <strong>desactivará</strong> de
                    todos los productos en los que se encuentre.
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{marginTop: '16px', fontWeight: 'bold'}}
                  >
                    Productos afectados:
                  </Typography>
                </>
              )}

              <List dense className={classes.modalList}>
                {products.map(product => {
                  return (
                    <ListItem
                      key={product.id}
                      button
                      onClick={() =>
                        window.open(`/admin/products/${product.id}`, '_blank')
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          alt={product.name}
                          src={getImageUrl(product.mainImageId)}
                        />
                      </ListItemAvatar>
                      <ListItemText primary={product.name} />
                    </ListItem>
                  );
                })}
              </List>
            </div>
          }
          onCancel={toggleConfirmModalOpen}
          onClose={toggleConfirmModalOpen}
          icon={<HelpOutline fontSize="large" />}
          onConfirm={() => {
            setLoading(true);
            deleteProductVariantOptionService(variantOption.id)
              .then(() => {
                setLoading(false);
                fetchProductVariantOptions();
              })
              .catch(() => {
                setLoading(false);
                showToast({
                  title: 'Error al eliminar la opción',
                  message: 'Inténtalo de nuevo por favor',
                  type: 'danger',
                  duration: 5000,
                });
              })
              .finally(() => {
                toggleConfirmModalOpen();
              });
          }}
        />
      )}
    </div>
  );
};

export default VariantItem;
